import { Check, ChevronDown, Menu as MenuIcon, Monitor, Moon, Sun } from 'lucide-react';

import { Avatar } from '@/components/ui/Avatar';
import { Menu, type MenuItem } from '@/components/ui/Menu';
import type { Theme } from '@/hooks/useTheme';
import { cn } from '@/lib/cn';

export interface TopBarProps {
  onOpenDrawer: () => void;
  drawerOpen: boolean;

  theme: Theme;
  onThemeChange: (theme: Theme) => void;

  language: string;
  languages: readonly { code: string; name: string }[];
  onLanguageChange: (code: string) => void;


  user: { name: string; email: string; roleLabel: string };
  isAdmin: boolean;
  onOpenProfile: () => void;
  onOpenOrganization: () => void;
  onSignOut: () => void;

  labels: {
    openMenu: string;
    language: string;
    theme: string;
    themeLight: string;
    themeDark: string;
    themeSystem: string;
    profileMenu: string;
    profile: string;
    organization: string;
    signOut: string;
  };
}

const THEME_ICON: Record<Theme, typeof Sun> = {
  light: Sun,
  dark: Moon,
  system: Monitor,
};

export function TopBar({
  onOpenDrawer,
  drawerOpen,
  theme,
  onThemeChange,
  language,
  languages,
  onLanguageChange,
  user,
  isAdmin,
  onOpenProfile,
  onOpenOrganization,
  onSignOut,
  labels,
}: TopBarProps) {
  const ThemeIcon = THEME_ICON[theme];

  const languageItems: MenuItem[] = languages.map((option) => ({
    key: option.code,
    label: option.name,
    icon: option.code === language ? <Check size={14} /> : undefined,
    onSelect: () => onLanguageChange(option.code),
  }));

  const themeItems: MenuItem[] = (['light', 'dark', 'system'] as const).map((option) => ({
    key: option,
    label: { light: labels.themeLight, dark: labels.themeDark, system: labels.themeSystem }[option],
    icon: theme === option ? <Check size={14} /> : <span className="w-3.5" aria-hidden />,
    onSelect: () => onThemeChange(option),
  }));

  const profileItems: MenuItem[] = [
    { key: 'profile', label: labels.profile, onSelect: onOpenProfile },
    ...(isAdmin
      ? [{ key: 'org', label: labels.organization, onSelect: onOpenOrganization }]
      : []),
    { key: 'signout', label: labels.signOut, destructive: true, onSelect: onSignOut },
  ];

  const chrome =
    'inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-neutral-200 text-neutral-800 hover:bg-neutral-100';

  return (
    <header className="sticky top-0 z-20 flex h-16 shrink-0 items-center gap-3 border-b border-neutral-200 bg-white px-4 sm:px-6 dark:bg-neutral-100">
      <button
        type="button"
        onClick={onOpenDrawer}
        aria-label={labels.openMenu}
        aria-expanded={drawerOpen}
        className={cn(chrome, 'w-9 lg:hidden')}
      >
        <MenuIcon size={18} />
      </button>

      <div className="flex-1" />

      <Menu
        ariaLabel={labels.language}
        trigger={
          <button type="button" aria-label={labels.language} className={cn(chrome, 'px-3')}>
            <span className="text-[13px] font-bold">{language.toUpperCase()}</span>
            <ChevronDown size={12} aria-hidden />
          </button>
        }
        items={languageItems}
      />

      <Menu
        ariaLabel={labels.theme}
        trigger={
          <button type="button" aria-label={labels.theme} className={cn(chrome, 'w-9')}>
            <ThemeIcon size={17} />
          </button>
        }
        items={themeItems}
      />

      <Menu
        ariaLabel={labels.profileMenu}
        trigger={
          <button type="button" aria-label={labels.profileMenu} className="rounded-full">
            <Avatar name={user.name} size="lg" className="bg-brand-600 text-white" />
          </button>
        }
        items={profileItems}
        header={
          <div className="flex flex-col gap-0.5 border-b border-neutral-200 px-2.5 py-2">
            <span className="text-[13px] font-bold text-neutral-900">{user.name}</span>
            <span className="truncate text-xs text-neutral-400">
              {user.email} · {user.roleLabel}
            </span>
          </div>
        }
      />
    </header>
  );
}
